import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  CircularProgress,
  Typography,
  Divider,
  Stack,
  LinearProgress,
  Chip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import dayjs from 'dayjs';

import { Iconify } from 'src/components/iconify';

import { getAllBranches, type BranchDto } from 'src/api/branch_api_data';
import {
  exportLeaveRequests,
  exportAllLeaveRequests,
  downloadFile,
  type ExportLeaveParams,
} from 'src/api/permissionApi';

// ----------------------------------------------------------------------

type ExportOption = 'current' | 'custom' | 'branch' | 'all';

interface Props {
  open: boolean;
  onClose: () => void;
  currentFilters?: {
    startDate?: string;
    endDate?: string;
    branchId?: string;
  };
  isSuperAdmin?: boolean;
}

export default function LeaveExportModal({ 
  open, 
  onClose, 
  currentFilters = {},
  isSuperAdmin = false 
}: Props) {
  const [format, setFormat] = useState<'PDF' | 'EXCEL'>('PDF');
  const [exportOption, setExportOption] = useState<ExportOption>('current');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  const [branchStartDate, setBranchStartDate] = useState<string>('');
  const [branchEndDate, setBranchEndDate] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [progress, setProgress] = useState<number>(0);
  const [branches, setBranches] = useState<BranchDto[]>([]);

  // Load branches on component mount
  useEffect(() => {
    const loadBranches = async () => {
      try {
        const branchData = await getAllBranches();
        setBranches(branchData);
      } catch (err) {
        console.error('Failed to load branches:', err);
      }
    };

    if (open) {
      loadBranches();
    }
  }, [open]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormat('EXCEL');
      setExportOption('current');
      setCustomStartDate('');
      setCustomEndDate('');
      setSelectedBranchId('');
      setBranchStartDate('');
      setBranchEndDate('');
      setError('');
      setSuccess('');
      setProgress(0);
      
      // Set default filename with Turkish timestamp
      const now = new Date();
      const turkishDate = now.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\./g, '_');
      const turkishTime = now.toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit'
      }).replace(':', '');
      
      const defaultFileName = `izin_talepleri_${turkishDate}_${turkishTime}`;
      setFileName(defaultFileName);
    }
  }, [open]);

  const handleExport = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setProgress(10);

      // Validation
      if (exportOption === 'custom') {
        if (!customStartDate || !customEndDate) {
          setError('Özel tarih aralığı için başlangıç ve bitiş tarihleri gereklidir');
          return;
        }
        if (dayjs(customStartDate).isAfter(dayjs(customEndDate))) {
          setError('Başlangıç tarihi bitiş tarihinden önce olmalıdır');
          return;
        }
      }

      if (exportOption === 'branch') {
        if (!selectedBranchId) {
          setError('Şube seçimi gereklidir');
          return;
        }
        if (!branchStartDate || !branchEndDate) {
          setError('Şube izinleri için başlangıç ve bitiş tarihleri gereklidir');
          return;
        }
        if (dayjs(branchStartDate).isAfter(dayjs(branchEndDate))) {
          setError('Başlangıç tarihi bitiş tarihinden önce olmalıdır');
          return;
        }
      }

      setProgress(30);

      // Prepare export parameters
      const params: ExportLeaveParams = {
        format,
        fileName: fileName || `izin_talepleri_${dayjs().format('YYYY_MM_DD')}`,
        exportType: exportOption, // Backend'e export tipini gönder
      };

      // Set date range based on selected option
      switch (exportOption) {
        case 'current':
          if (currentFilters.startDate) params.startDate = currentFilters.startDate;
          if (currentFilters.endDate) params.endDate = currentFilters.endDate;
          if (currentFilters.branchId) params.branchId = currentFilters.branchId;
          break;
        
        case 'custom':
          params.startDate = customStartDate;
          params.endDate = customEndDate;
          break;
        
        case 'branch':
          params.startDate = branchStartDate;
          params.endDate = branchEndDate;
          params.branchId = selectedBranchId;
          break;
        
        case 'all':
          // No date or branch filters for all data
          break;
        
        default:
          // Default to current filters
          if (currentFilters.startDate) params.startDate = currentFilters.startDate;
          if (currentFilters.endDate) params.endDate = currentFilters.endDate;
          if (currentFilters.branchId) params.branchId = currentFilters.branchId;
          break;
      }

      setProgress(60);

      // Call appropriate API based on user role and export option
      let blob: Blob;
      if (isSuperAdmin && exportOption === 'all') {
        blob = await exportAllLeaveRequests(params);
      } else {
        blob = await exportLeaveRequests(params);
      }

      setProgress(80);

      // Generate filename with extension
      const extension = format === 'PDF' ? 'pdf' : 'xlsx';
      const finalFileName = `${params.fileName}.${extension}`;

      // Download file
      downloadFile(blob, params.fileName || 'izin_talepleri', format);

      setProgress(100);
      setSuccess(`${format} dosyası başarıyla indirildi!`);

      // Close modal after delay
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error('Export failed:', err);
      setError(err.message || 'Export işlemi sırasında bir hata oluştu');
    } finally {
      setLoading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  }, [
    format,
    exportOption,
    customStartDate,
    customEndDate,
    selectedBranchId,
    branchStartDate,
    branchEndDate,
    fileName,
    currentFilters,
    isSuperAdmin,
    onClose,
  ]);

  const renderExportOptions = () => (
    <Box>
      <FormLabel component="legend" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>
        Export Kapsamı
      </FormLabel>
      <RadioGroup
        value={exportOption}
        onChange={(e) => setExportOption(e.target.value as ExportOption)}
        sx={{ gap: 1 }}
      >
        <FormControlLabel
          value="current"
          control={<Radio color="primary" />}
          label="Mevcut Filtreler (Şu anda görünen izin talepleri)"
        />
        <FormControlLabel
          value="custom"
          control={<Radio color="primary" />}
          label={
            exportOption === 'custom' && customStartDate && customEndDate
              ? `Özel Tarih Aralığı (${dayjs(customStartDate).format('DD/MM/YYYY')} - ${dayjs(customEndDate).format('DD/MM/YYYY')})`
              : "Özel Tarih Aralığı (Belirli tarih aralığındaki izinler)"
          }
        />
        <FormControlLabel
          value="branch"
          control={<Radio color="primary" />}
          label={
            exportOption === 'branch' && selectedBranchId
              ? `Şube Bazında (${branches.find(b => b.id === selectedBranchId)?.name || 'Belirli şubenin izinleri'})`
              : "Şube Bazında (Belirli şubenin izinleri)"
          }
        />
        {isSuperAdmin && (
          <FormControlLabel
            value="all"
            control={<Radio color="primary" />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>Tüm İzin Talepleri</Typography>
                <Chip 
                  label="Super Admin" 
                  size="small" 
                  color="warning" 
                  variant="outlined"
                />
              </Box>
            }
          />
        )}
      </RadioGroup>
    </Box>
  );

  const renderDateInputs = () => {
    if (exportOption === 'custom') {
      return (
        <Stack spacing={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            Tarih Aralığı Seçimi
          </Typography>
          <Box display="flex" gap={2}>
            <TextField
              type="date"
              label="Başlangıç Tarihi"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <Iconify icon="eva:calendar-fill" width={16} sx={{ mr: 1, color: 'text.secondary' }} />
                )
              }}
            />
            <TextField
              type="date"
              label="Bitiş Tarihi"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <Iconify icon="eva:calendar-fill" width={16} sx={{ mr: 1, color: 'text.secondary' }} />
                )
              }}
            />
          </Box>
        </Stack>
      );
    }

    if (exportOption === 'branch') {
      return (
        <Stack spacing={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            Şube ve Tarih Seçimi
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Şube Seçin</InputLabel>
            <Select
              value={selectedBranchId}
              onChange={(e) => setSelectedBranchId(e.target.value)}
              label="Şube Seçin"
            >
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Iconify icon="eva:pin-fill" width={16} />
                    <Typography>{branch.name}</Typography>
                    {branch.companyName && (
                      <Typography variant="caption" color="text.secondary">
                        ({branch.companyName})
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box display="flex" gap={2}>
            <TextField
              type="date"
              label="Başlangıç Tarihi"
              value={branchStartDate}
              onChange={(e) => setBranchStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <Iconify icon="eva:calendar-fill" width={16} sx={{ mr: 1, color: 'text.secondary' }} />
                )
              }}
            />
            <TextField
              type="date"
              label="Bitiş Tarihi"
              value={branchEndDate}
              onChange={(e) => setBranchEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <Iconify icon="eva:calendar-fill" width={16} sx={{ mr: 1, color: 'text.secondary' }} />
                )
              }}
            />
          </Box>
        </Stack>
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: 'primary.lighter',
                color: 'primary.main',
              }}
            >
              <Iconify icon="eva:calendar-outline" width={24} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              İzin Talepleri Export
            </Typography>
          </Box>
          <Button
            onClick={onClose}
            size="small"
            sx={{ minWidth: 0, p: 1, borderRadius: '50%' }}
            disabled={loading}
          >
            <Iconify icon="eva:close-fill" width={20} />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3}>
          {/* Progress Bar */}
          {loading && (
            <Box>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                  }
                }} 
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Export işlemi: %{progress}
              </Typography>
            </Box>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" variant="filled">
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert severity="success" variant="filled">
              {success}
            </Alert>
          )}

          {/* Format Selection */}
          <Box>
            <FormLabel component="legend" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>
              Dosya Formatı
            </FormLabel>
            <RadioGroup
              row
              value={format}
              onChange={(e) => setFormat(e.target.value as 'PDF' | 'EXCEL')}
              sx={{ gap: 2 }}
            >
              <FormControlLabel
                value="EXCEL"
                control={<Radio color="primary" />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Iconify icon="vscode-icons:file-type-excel" width={20} />
                    <Typography>Excel</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="PDF"
                control={<Radio color="primary" />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Iconify icon="vscode-icons:file-type-pdf2" width={20} />
                    <Typography>PDF</Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </Box>

          <Divider />

          {/* Export Options */}
          {renderExportOptions()}

          {/* Date Inputs */}
          {renderDateInputs() && <Box>{renderDateInputs()}</Box>}

          {/* File Name Input */}
          <Box>
            <FormLabel component="legend" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>
              Dosya Adı
            </FormLabel>
            <TextField
              fullWidth
              label="Dosya Adı"
              value={fileName}
              onChange={(e) => {
                const value = e.target.value;
                // Remove extension if user adds it
                const cleanValue = value.replace(/\.(pdf|xlsx|xls)$/i, '');
                setFileName(cleanValue);
              }}
              InputProps={{
                startAdornment: (
                  <Iconify icon="eva:file-text-outline" width={16} sx={{ mr: 1, color: 'text.secondary' }} />
                ),
                endAdornment: (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    .{format === 'PDF' ? 'pdf' : 'xlsx'}
                  </Typography>
                ),
              }}
              helperText="Dosya uzantısı otomatik olarak eklenecektir"
            />
          </Box>

          {/* Export Summary */}
          <Box 
            sx={{ 
              p: 2, 
              bgcolor: 'background.neutral', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Export Özeti
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2">
                <strong>Format:</strong> {format === 'PDF' ? 'PDF' : 'Excel'}
              </Typography>
              <Typography variant="body2">
                <strong>Dosya:</strong> {fileName}.{format === 'PDF' ? 'pdf' : 'xlsx'}
              </Typography>
              <Typography variant="body2">
                <strong>Kapsam:</strong> {
                  exportOption === 'current' ? 'Mevcut filtreli veriler' :
                  exportOption === 'custom' ? 'Özel tarih aralığı' :
                  exportOption === 'branch' ? 'Şube bazında' :
                  'Tüm izin talepleri'
                }
              </Typography>
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          size="large"
          sx={{ minWidth: 100 }}
        >
          İptal
        </Button>
        <LoadingButton
          onClick={handleExport}
          variant="contained"
          loading={loading}
          loadingPosition="start"
          startIcon={<Iconify icon="eva:download-outline" width={20} />}
          size="large"
          sx={{ minWidth: 140 }}
        >
          {loading ? 'Export Ediliyor...' : 'Export Et'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
} 